import { Check } from "lucide-react";
import { Eyebrow, MaskHeading, Reveal, Gold } from "./Motion";
import { PRODUCTS } from "./data";

const spotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Extras = ({ p }) => (
  <div className="mt-5 flex flex-wrap gap-2">
    {p.chips.map((c) => (
      <span key={c.name} data-testid={`chip-${p.key}-${slug(c.name)}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${c.cls ?? "border-ns-emerald/15 text-ns-muted"}`}>
        {c.name}
      </span>
    ))}
  </div>
);

const ProductCard = ({ p, i }) => (
  <Reveal delay={(i % 2) * 0.12} className={`h-full ${p.span}`}>
    <article
      onMouseMove={spotlight}
      data-testid={`product-card-${p.key}`}
      className="spotlight group relative flex h-full flex-col overflow-hidden rounded-[32px] bg-white p-3 shadow-[0_1px_0_rgba(11,60,42,0.06)] transition-shadow duration-500 hover:shadow-[0_40px_80px_-40px_rgba(11,60,42,0.45)]"
    >
      <div className="relative h-[300px] overflow-hidden rounded-[24px] sm:h-[360px] lg:h-[400px]">
        <img src={p.image} alt={p.name} loading="lazy" style={{ objectPosition: p.pos }} className={`h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.07] ${p.soon ? "saturate-[0.85]" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-ns-night/50 via-transparent to-transparent" />
        <span data-testid={`product-label-${p.key}`} className="absolute left-4 top-4 max-w-[62%] rounded-full bg-ns-ivory/90 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-ns-emerald backdrop-blur">
          {p.label}
        </span>
        {p.soon && (
          <span className="absolute bottom-4 right-4 rounded-full border border-ns-gold bg-ns-emerald/85 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-ns-gold backdrop-blur">Coming Soon</span>
        )}
      </div>
      <div className="flex flex-1 flex-col px-4 pb-5 pt-6 sm:px-5">
        <span className="font-sans text-xs font-bold tracking-[0.2em] text-ns-bronze">0{i + 1}</span>
        <h3 data-testid={`product-name-${p.key}`} className="mt-2 font-serif text-3xl font-semibold leading-tight text-ns-emerald sm:text-[2.1rem]">{p.name}</h3>
        <p className="mt-2 text-[15px] text-ns-muted">{p.text}</p>
        {p.chips && <Extras p={p} />}
        <ul className="mt-auto flex flex-wrap gap-x-6 gap-y-2 border-t border-ns-emerald/10 pt-5" style={{ marginTop: "auto" }}>
          {p.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm font-medium text-ns-ink">
              <Check className="h-4 w-4 text-ns-bronze" /> {f}
            </li>
          ))}
        </ul>
      </div>
    </article>
  </Reveal>
);

export const Products = () => (
  <section id="products" data-testid="products-section" className="bg-ns-cream py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow testId="products-eyebrow">02 / Products</Eyebrow>
          <MaskHeading
            testId="products-heading"
            className="mt-6 font-serif text-[2.6rem] font-semibold leading-[1.02] text-ns-emerald sm:text-6xl lg:text-7xl"
            lines={[<span key="a">A little more <Gold>goodness</Gold></span>, "in every aisle."]}
          />
        </div>
        <Reveal className="lg:col-span-4 lg:col-start-9">
          <p data-testid="products-supporting-copy" className="text-base leading-relaxed text-ns-muted">
            Thoughtfully made products for modern Indian households, retail counters, and growing distribution networks.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-5 lg:grid-cols-12">
        {PRODUCTS.map((p, i) => <ProductCard key={p.key} p={p} i={i} />)}
      </div>
    </div>
  </section>
);
